import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
} from '@web/shared/ui';
import {
  AdminCosmeticCollectionFilter,
  AdminCosmeticCollectionItem,
  AdminCosmeticDataSourceService,
} from '@web/shared/module/cosmetic';
import { AdminCosmeticListItemAction } from '../../enums/admin-cosmetic-list-item-action';
import { AdminCosmeticListCollectionAction } from '../../enums/admin-cosmetic-list-collection-action';
import { AdminCosmeticListAction } from '../../enums/admin-cosmetic-list-action';
import { Observable, of } from 'rxjs';
import { Route, RouteParam } from '@web/common';
import { once } from '@web/utils';
import { AdminCosmeticListCustomCellTemplateComponent } from '../../components/admin-cosmetic-list-custom-cell-template/admin-cosmetic-list-custom-cell-template.component';
import { AdminCosmeticStatusProviderService } from '../../providers/admin-cosmetic-status-provider.service';

@Component({
  selector: 'app-admin-cosmetic-list',
  imports: [TableComponent, AdminCosmeticListCustomCellTemplateComponent],
  templateUrl: './admin-cosmetic-list.component.html',
  styleUrl: './admin-cosmetic-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminCosmeticListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminCosmeticCollectionItem, AdminCosmeticCollectionFilter, AdminCosmeticListItemAction, AdminCosmeticListCollectionAction, AdminCosmeticListAction>).create();
  private readonly cosmeticDataSource = inject(AdminCosmeticDataSourceService);
  /* prettier-ignore */
  private readonly cosmeticStatusProvider = inject(AdminCosmeticStatusProviderService);
  private readonly table = inject(TableService);

  public readonly addCosmeticVisibility = input<boolean>(false);

  protected config!: TableConfig<
    AdminCosmeticCollectionItem,
    AdminCosmeticCollectionFilter,
    AdminCosmeticListItemAction,
    AdminCosmeticListCollectionAction,
    AdminCosmeticListAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .actions((builder) => [
        builder
          .initRouterLink(AdminCosmeticListAction.Create)
          .label('Dodaj kosmetyk')
          .visibility(() => of(this.addCosmeticVisibility()))
          .routerLink((provider) => provider.routerLink(Route.CosmeticCreate))
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminCosmeticListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.CosmeticDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initRouterLink(AdminCosmeticListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Edycja')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.CosmeticEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initCommand(AdminCosmeticListItemAction.Remove)
          .visibility((item) => item.canBeRemoved)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .command((item) => this.remove(item))
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('slug')
          .build(),

        builder //
          .initText('cosmeticBrandName')
          .build(),

        builder //
          .initText('name')
          .build(),

        builder //
          .initSelect('status')
          .dictionary(() => this.cosmeticStatusProvider.selectItems())
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initCustom('featuredFileUrl')
          .width('9rem')
          .label('Zdjęcie główne')
          .build(),

        builder //
          .initText('name')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .build(),

        builder //
          .initText('cosmeticBrandName')
          .label('Nazwa marki')
          .sortable(true)
          .sortKey('cosmeticBrandName')
          .filterable(true)
          .filterKey('cosmeticBrandName')
          .build(),

        builder //
          .initCurrency('price')
          .label('Cena')
          .sortable(true)
          .sortKey('price')
          .filterable(false)
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .build(),

        builder //
          .initCustom('status')
          .filterKey('status')
          .filterable(true)
          .label('Status')
          .sortKey('status')
          .sortable(true)
          .width('20rem')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminCosmeticCollectionFilter>,
        ): Observable<TableDataPayload<AdminCosmeticCollectionItem>> =>
          this.cosmeticDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private remove(item: AdminCosmeticCollectionItem): void {
    this.cosmeticDataSource
      .removeById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private refresh(): void {
    this.table.refresh();
  }
}
